import { useCallback, useEffect, useState, useSyncExternalStore } from 'react'

/**
 * Read cache (Speed and Feel D1). Screens show the last data they had at once and refresh it in the background.
 * Lives outside React, so it survives the idle lock (which unmounts the app shell) and, for the keys in PERSIST,
 * a relaunch. Holds lists and counts only: never a draft sale, trade or intake, and never a session.
 * Writes still go straight to the server; each write refreshes the keys it touches, as the screens did before.
 */
const REF = String(import.meta.env.VITE_SUPABASE_URL ?? '').replace(/^https?:\/\//, '').split('.')[0]
const NS = `starchive.cache.v1:${REF}:`
const PERSIST = /^(employees|consignors|customers|brands|mode|tax|sales|today:.*|last7|rack|pending|qa:queue:.*)$/

interface Entry { data: unknown; at: number }
const mem = new Map<string, Entry>()
const inflight = new Map<string, Promise<unknown>>()
const listeners = new Map<string, Set<() => void>>()

function read(key: string): Entry | undefined {
  const m = mem.get(key)
  if (m || !PERSIST.test(key)) return m
  try {
    const raw = localStorage.getItem(NS + key)
    if (!raw) return undefined
    const e = JSON.parse(raw) as Entry
    mem.set(key, e)
    return e
  } catch { return undefined }
}
function write(key: string, data: unknown) {
  const e = { data, at: Date.now() }
  mem.set(key, e)
  if (PERSIST.test(key)) { try { localStorage.setItem(NS + key, JSON.stringify(e)) } catch { /* full or private mode: memory only */ } }
  listeners.get(key)?.forEach((f) => f())
}

export function getCached<T>(key: string): T | undefined { return read(key)?.data as T | undefined }
/** When a key was last written (ms since epoch), or undefined when it is not cached. */
export function cachedAt(key: string): number | undefined { return read(key)?.at }
/** Forget one key (its data must be read again next time). */
export function dropCached(key: string) {
  mem.delete(key)
  if (PERSIST.test(key)) { try { localStorage.removeItem(NS + key) } catch { /* ignore */ } }
  listeners.get(key)?.forEach((f) => f())
}
/** Replace a cached value (an optimistic update, or putting the old value back when a write fails). */
export function setCached<T>(key: string, v: T | ((prev: T | undefined) => T)) {
  write(key, typeof v === 'function' ? (v as (p: T | undefined) => T)(getCached<T>(key)) : v)
}
/** Load a key from the server now (one request at a time per key) and cache the answer. */
export function fetchCached<T>(key: string, loader: () => Promise<T>): Promise<T> {
  const running = inflight.get(key)
  if (running) return running as Promise<T>
  const p = loader().then((d) => { write(key, d); return d }).finally(() => inflight.delete(key))
  inflight.set(key, p)
  return p
}
/** Warm a key if it is missing or older than staleMs; errors are ignored (the screen will try again when it opens). */
export function prefetch<T>(key: string, loader: () => Promise<T>, staleMs = 30_000) {
  const e = read(key)
  if (e && Date.now() - e.at < staleMs) return
  fetchCached(key, loader).catch(() => {})
}
/** Forget everything (store login signed out). */
export function clearCache() {
  mem.clear()
  try { Object.keys(localStorage).filter((k) => k.startsWith('starchive.cache.v1:')).forEach((k) => localStorage.removeItem(k)) } catch { /* ignore */ }
}

function subscribe(key: string, f: () => void) {
  let s = listeners.get(key); if (!s) listeners.set(key, (s = new Set()))
  s.add(f)
  return () => { s!.delete(f) }
}

/** The cached value at once, refreshed from the server when missing or older than staleMs (0 = every time the screen opens). */
export function useCached<T>(key: string, loader: () => Promise<T>, opts: { staleMs?: number; enabled?: boolean } = {}) {
  const { staleMs = 30_000, enabled = true } = opts
  const entry = useSyncExternalStore(useCallback((f) => subscribe(key, f), [key]), () => read(key))
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const refresh = useCallback(async () => {
    setLoading(true); setError('')
    try { return await fetchCached(key, loader) } catch (e) { setError((e as Error).message); return undefined } finally { setLoading(false) }
  }, [key]) // eslint-disable-line react-hooks/exhaustive-deps -- loaders are module functions or keyed by `key`
  useEffect(() => {
    if (!enabled) return
    const e = read(key)
    if (!e || Date.now() - e.at >= staleMs) void refresh()
  }, [key, enabled]) // eslint-disable-line react-hooks/exhaustive-deps -- revalidate when the screen opens or the key changes
  return { data: entry?.data as T | undefined, at: entry?.at ?? 0, error, loading, refresh }
}
